import {
  AppCard,
  Button,
  FormStaticAutocomplete,
  FormTextField,
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiContentSaveOutline, mdiExclamation } from "@mdi/js";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { Material } from "../../../../../@types/services/Material";
import { MaterialNumberField } from "../../../../../components/molecules/MaterialNumberField";
import { qualityDeviationOptions } from "../../../../../constants/mmRequest/qualityDeviationOptions";
import { rsps3SubjectOptions } from "../../../../../constants/mmRequest/rsps3SubjectOptions";
import { ForbiddenError } from "../../../../../errors/ForbiddenError";
import {
  fetchRsps3Request,
  updateRsps3Request,
} from "../../../../../services/rsps3Requests";

type FormValues = {
  mm_request_attributes: {
    id: number;
    matnumber: string[];
    subject: string;
    description: string;
    quality_deviation: string[];
    quality_deviation_description: string;
  };
  old_part_number: string;
  new_part_number: string;
  quantity_of_tags: string;
};

const EditRsps3Request: React.FC = () => {
  const { t } = useTranslation();

  const { rsps3RequestId } = Route.useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [formData, setFormData] = useState<FormValues | null>(null);
  const [mmRequestId, setMmRequestId] = useState<number | null>(null);

  const { handleSubmit, control, reset, setValue, watch } = useForm<FormValues>(
    {
      defaultValues: {
        mm_request_attributes: {
          description: "",
          id: 0,
          matnumber: [],
          quality_deviation: [],
          quality_deviation_description: "",
          subject: "",
        },
        new_part_number: "",
        old_part_number: "",
        quantity_of_tags: "",
      },
    },
  );

  const subject = watch("mm_request_attributes.subject");
  const title = mmRequestId
    ? `${t("common.rsps3Request")} #${mmRequestId}`
    : t("common.rsps3Request");

  const handleClose = useCallback(() => {
    setOpenConfirmDialog(false);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!formData) return;

    handleClose();
    setLoadingSubmit(true);

    const formattedData = {
      mm_request_attributes: {
        backup_requester_id: "",
        description: formData.mm_request_attributes.description,
        file_attachments: [],
        id: formData.mm_request_attributes.id,
        matnumber: formData.mm_request_attributes.matnumber.map((material) =>
          typeof material === "string"
            ? material
            : (material as Material).matnumber,
        ),
        quality_deviation: formData.mm_request_attributes.quality_deviation,
        quality_deviation_description:
          formData.mm_request_attributes.quality_deviation_description,
        subject: formData.mm_request_attributes.subject,
      },
      new_part_number: formData.new_part_number,
      old_part_number: formData.old_part_number,
      quantity_of_tags: formData.quantity_of_tags,
    };

    await updateRsps3Request(rsps3RequestId, formattedData)
      .then(() => {
        enqueueSnackbar(t("rsps3Requests.requestUpdated"), {
          variant: "success",
        });
        navigate({
          params: { rsps3RequestId },
          to: "/vbuyer/rsps3_requests/$rsps3RequestId",
        });
      })
      .catch(() => {
        setLoadingSubmit(false);
        enqueueSnackbar(t("rsps3Requests.errorMessage"), {
          variant: "error",
        });
      });
  }, [formData, handleClose, rsps3RequestId, navigate, t]);

  const onSubmit: SubmitHandler<FormValues> = useCallback(async (data) => {
    setFormData(data);
    setOpenConfirmDialog(true);
  }, []);

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      try {
        if (rsps3RequestId) {
          const rsps3Request = await fetchRsps3Request(rsps3RequestId);
          setMmRequestId(rsps3Request.mm_request.id);
          reset({
            mm_request_attributes: {
              description: rsps3Request.mm_request.description || "",
              id: rsps3Request.mm_request.id,
              matnumber: rsps3Request.mm_request.matnumber || [],
              quality_deviation:
                rsps3Request.mm_request.quality_deviation || [],
              quality_deviation_description:
                rsps3Request.mm_request.quality_deviation_description || "",
              subject: rsps3Request.mm_request.subject || "",
            },
            new_part_number: rsps3Request.new_part_number || "",
            old_part_number: rsps3Request.old_part_number || "",
            quantity_of_tags: rsps3Request.quantity_of_tags || "",
          });
        }
      } catch {
        enqueueSnackbar(t("rsps3Requests.errorMessage"), {
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [rsps3RequestId, reset, t]);

  if (loading) {
    return (
      <PageContainer
        backTo={`/vbuyer/rsps3_requests/${rsps3RequestId}`}
        maxWidth="md"
        showBackButton
        title={title}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  return (
    <>
      <PageContainer
        backTo={`/vbuyer/rsps3_requests/${rsps3RequestId}`}
        maxWidth="md"
        showBackButton
        title={title}
      >
        <AppCard>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                width: "100%",
              }}
            >
              <MaterialNumberField
                control={control}
                helperText={t("valorationRequests.materialHelper")}
                label={t("valorationRequests.material")}
                name="mm_request_attributes.matnumber"
                rules={{ required: t("valorationRequests.requiredMessage") }}
                setValue={setValue}
              />

              <FormStaticAutocomplete
                control={control}
                getOptionLabel={(option) =>
                  t(`rsps3Requests.subjects.${option}`)
                }
                label={t("rsps3Requests.subject")}
                name="mm_request_attributes.subject"
                options={rsps3SubjectOptions}
              />

              {subject === "part_number_replacement" && (
                <>
                  <FormTextField
                    control={control}
                    label={t("rsps3Requests.oldPartNumber")}
                    name="old_part_number"
                    type="number"
                  />

                  <FormTextField
                    control={control}
                    label={t("rsps3Requests.newPartNumber")}
                    name="new_part_number"
                    type="number"
                  />
                </>
              )}

              <FormTextField
                control={control}
                label={t("rsps3Requests.quantityOfTags")}
                name="quantity_of_tags"
                type="number"
              />

              <FormTextField
                control={control}
                helperText={t("mmRequests.descriptionHelper")}
                label={t("valorationRequests.description")}
                multiline
                name="mm_request_attributes.description"
                rows={6}
              />

              <FormStaticAutocomplete
                control={control}
                getOptionLabel={(option) =>
                  option ? t(`mmRequests.qualityDeviation.${option}`) : ""
                }
                label={t("valorationRequests.qualityDeviation")}
                multiple
                name="mm_request_attributes.quality_deviation"
                options={qualityDeviationOptions}
              />

              <FormTextField
                control={control}
                label={t("valorationRequests.qualityDeviationDescription")}
                multiline
                name="mm_request_attributes.quality_deviation_description"
                rows={3}
              />

              <Button
                color="success"
                disabled={loadingSubmit}
                startIcon={
                  loadingSubmit ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : (
                    <Icon icon={mdiContentSaveOutline} />
                  )
                }
                sx={{
                  alignSelf: "flex-end",
                  maxWidth: "100%",
                  minWidth: 0,
                  width: "fit-content",
                }}
                type="submit"
                variant="contained"
              >
                {t("common.save")}
              </Button>
            </Box>
          </form>
        </AppCard>
      </PageContainer>
      <Dialog
        aria-describedby="alert-dialog-description"
        aria-labelledby="alert-dialog-title"
        onClose={handleClose}
        open={openConfirmDialog}
        slotProps={{
          paper: {
            sx: {
              pt: 4,
              px: 2,
              textAlign: "center",
              width: "600px",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 3,
            mt: -1,
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              backgroundColor: "white",
              border: "3px solid",
              borderColor: "#ED6C02",
              borderRadius: "50%",
              color: "#ED6C02",
              display: "flex",
              height: "80px",
              justifyContent: "center",
              width: "80px",
            }}
          >
            <Icon fontSize="large" icon={mdiExclamation} />
          </Box>
        </Box>

        <DialogTitle sx={{ textAlign: "center" }}>
          {t("rsps3Requests.modalTitle")}
        </DialogTitle>

        <DialogContent>
          <DialogContentText
            sx={{ textAlign: "center", whiteSpace: "pre-line" }}
          >
            {t("rsps3Requests.modalMessage")}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>{t("cancel")}</Button>
          <Button autoFocus onClick={handleConfirm}>
            {t("ok")}
          </Button>
        </DialogActions>
      </Dialog>
      {!!loadingSubmit && (
        <Dialog open>
          <DialogContent sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress />
            <DialogContentText sx={{ mt: 2 }}>
              {t("rsps3Requests.dialog.processing")}
            </DialogContentText>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/rsps3_requests/$rsps3RequestId/edit",
)({
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Edit material requests")) {
      return;
    }

    throw new ForbiddenError();
  },
  component: EditRsps3Request,
});
